import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  HStack,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, { FC, useRef, useState } from 'react';
import { FaEye } from 'react-icons/fa';
import { findWithdrawel } from 'services/api.service';
import { maskDocument } from 'utils/formatters';
import { maskCurrency } from 'utils/number';
import { Withdrawal } from '..';
import { saveAs } from 'file-saver';
import { DateTime } from 'luxon';
import { useReactToPrint } from 'react-to-print';
import PrintWithdrawel from './PrintWithdrawel';
import { FiPrinter, FiDownload } from 'react-icons/fi';
import { toPng } from 'html-to-image';
import { maskAccountNumber } from 'utils/account';
import ScheduledWithdrawalAlert from 'components/ScheduledWithdrawalAlert';
import { formatRecipientDocument, getBilletBeneficiaries, getWithdrawalRecipient } from '../withdrawalRecipient';

const LabelItem: FC<{
  label: string;
  value: string;
}> = ({ label, value }) => {
  return (
    <VStack
      mb={3}
      width="100%"
      alignItems="start"
    >
      <Text
        as={'small'}
        fontSize="sm"
        color="gray.600"
      >
        {label}
      </Text>
      <Text
        mt={'0px !important'}
        width="100%"
        fontSize="md"
        overflow="hidden"
        noOfLines={30}
      >
        {value || '---'}
      </Text>
    </VStack>
  );
};

const SeeWithdrawel: React.FC<{
  data: Withdrawal;
}> = ({ data: dataWithdraw }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [data, setData] = useState(dataWithdraw);

  const componentRef = useRef<any>();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const downloadImage = async () => {
    if (!componentRef.current) {
      toast({
        title: 'Erro',
        description: 'Não há dados do comprovante para gerar',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    const png = await toPng(componentRef.current, {
      pixelRatio: 2,
      backgroundColor: 'white',
      quality: 1,
    });
    saveAs(png, `comprovante-${data.id}.png`);
  };

  const recipient = getWithdrawalRecipient(data);
  const billet = getBilletBeneficiaries(data);
  const isPix = data.type === 'DICT' || data.type === 'MANU' || data.type === 'QRCODE';

  return (
    <>
      <IconButton
        aria-label="Ver Saque"
        size="sm"
        variant="outline"
        icon={<FaEye />}
        onClick={() => {
          findWithdrawel(data.id).then((res) => {
            setData(res);
          });
          onOpen();
        }}
      />
      {isOpen && (
        <>
          <Modal
            isOpen={isOpen}
            onClose={onClose}
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Verificar Saque</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Box
                  position="fixed"
                  left="-10000px"
                  top={0}
                  pointerEvents="none"
                  aria-hidden
                >
                  <PrintWithdrawel
                    withdraw={data}
                    ref={componentRef}
                  />
                </Box>
                {data.status === 'PENDING' && <ScheduledWithdrawalAlert />}
                {data.status === 'IN_PAYMENT' && (
                  <Alert status="info" borderRadius="md" mb={4}>
                    <AlertIcon />
                    <Text fontSize="sm">
                      O pagamento será processado em até <b>24 horas</b>.
                    </Text>
                  </Alert>
                )}
                <SimpleGrid columns={{ base: 1 }}>
                  <LabelItem
                    label="Cliente"
                    value={data.Customer?.name}
                  />
                  <LabelItem
                    label="Documento"
                    value={maskDocument(data.Customer?.document_number)}
                  />
                  <LabelItem
                    label="Valor do Saque"
                    value={maskCurrency(data.amount)}
                  />{' '}
                  <LabelItem
                    label="Valor da Taxa"
                    value={maskCurrency(data.fee)}
                  />{' '}
                  <LabelItem
                    label="Valor Final"
                    value={maskCurrency(Math.abs(+data.amount) + Math.abs(+data.fee))}
                  />
                  <LabelItem
                    label="Descrição"
                    value={data.description}
                  />
                </SimpleGrid>

                {data.type === 'BILLET' ? (
                  <>
                    <Text fontWeight="semibold">Dados Boleto</Text>
                    <Divider mb={2} />
                    <SimpleGrid columns={{ base: 1 }}>
                      <LabelItem
                        label="Beneficiário"
                        value={billet.name ?? recipient.name}
                      />
                      <LabelItem
                        label="CPF/CNPJ do beneficiário"
                        value={formatRecipientDocument(billet.name ? billet.document : recipient.document)}
                      />
                      {billet.final && (
                        <>
                          <LabelItem
                            label="Beneficiário final"
                            value={billet.final.name}
                          />
                          <LabelItem
                            label="CPF/CNPJ do beneficiário final"
                            value={formatRecipientDocument(billet.final.document)}
                          />
                        </>
                      )}
                      <LabelItem
                        label="Data de Vencimento"
                        value={data.dueDate ? DateTime.fromISO(data.dueDate).toFormat('dd/MM/yyyy') : '---'}
                      />
                      <LabelItem
                        label="Boleto"
                        value={data.billetCode}
                      />
                      <Button
                        colorScheme="green"
                        size={'sm'}
                        variant={'outline'}
                        onClick={() => {
                          navigator.clipboard.writeText(data.billetCode);
                          toast({
                            title: 'Sucesso',
                            description: `Boleto copiado com sucesso`,
                            status: 'success',
                            duration: 9000,
                            isClosable: true,
                          });
                        }}
                      >
                        Copiar
                      </Button>
                    </SimpleGrid>
                  </>
                ) : undefined}
                {data.type === 'QRCODE' ? (
                  <Box maxW="100%">
                    <Text fontWeight="semibold">Pix Copia e Cola</Text>
                    <Divider mb={2} />
                    <SimpleGrid columns={{ base: 1 }}>
                      <LabelItem
                        label="Copia e Cola"
                        value={data.billetCode}
                      />
                    </SimpleGrid>
                  </Box>
                ) : undefined}
                {isPix && !data.receiver ? (
                  <>
                    <Text fontWeight="semibold">Favorecido</Text>
                    <Divider mb={2} />
                    <SimpleGrid columns={{ base: 1 }}>
                      <LabelItem
                        label="Nome"
                        value={recipient.name}
                      />
                      <LabelItem
                        label="CPF/CNPJ"
                        value={formatRecipientDocument(recipient.document)}
                      />
                      {(recipient.bank || data.CustomerAccounts?.bankIspb) && (
                        <LabelItem
                          label="Banco"
                          value={recipient.bank ?? data.CustomerAccounts?.bankIspb}
                        />
                      )}
                      {data.CustomerAccounts?.agency && (
                        <LabelItem
                          label="Agência"
                          value={data.CustomerAccounts.agency}
                        />
                      )}
                      {data.CustomerAccounts?.account && (
                        <LabelItem
                          label="Conta"
                          value={data.CustomerAccounts.account}
                        />
                      )}
                      {data.type !== 'QRCODE' && data.CustomerAccounts?.pix && (
                        <LabelItem
                          label="Chave Pix"
                          value={data.CustomerAccounts.pix}
                        />
                      )}
                    </SimpleGrid>
                  </>
                ) : undefined}
                {data.receiver ? (
                  <>
                    <Text fontWeight="semibold">Dados da Conta</Text>
                    <Divider mb={2} />
                    <SimpleGrid columns={{ base: 1 }}>
                      <>
                        <LabelItem
                          label="Nome"
                          value={data.receiver?.name}
                        />
                        <LabelItem
                          label="Conta"
                          value={maskAccountNumber(data.receiver?.accountNumber)}
                        />
                        <LabelItem
                          label="Documento"
                          value={maskDocument(data.receiver?.document_number)}
                        />
                      </>
                    </SimpleGrid>
                  </>
                ) : undefined}
                {data?.withdrawSulcredi && (
                  <Box
                    mt={4}
                    border="ButtonShadow"
                  >
                    <Text
                      fontWeight="semibold"
                      mb={4}
                    >
                      Comprovante
                    </Text>
                    <HStack spacing={2} mb={2}>
                      <Tooltip
                        label={`Imprimir`}
                        placement={`top`}
                      >
                        <IconButton
                          aria-label="Imprimir"
                          icon={<FiPrinter />}
                          size={`sm`}
                          color={`gray.500`}
                          variant={`outline`}
                          onClick={handlePrint}
                          disabled={data?.withdrawSulcredi?.status !== 2 && data.status !== 'IN_PAYMENT'}
                        />
                      </Tooltip>
                      <Tooltip
                        label={`Baixar imagem`}
                        placement={`top`}
                      >
                        <IconButton
                          aria-label="Baixar imagem"
                          icon={<FiDownload />}
                          size={`sm`}
                          color={`gray.500`}
                          variant={`outline`}
                          onClick={downloadImage}
                          disabled={data?.withdrawSulcredi?.status !== 2 && data.status !== 'IN_PAYMENT'}
                        />
                      </Tooltip>
                    </HStack>
                    <LabelItem
                      label="Status Pagamento"
                      value={data?.withdrawSulcredi?.status === 2 ? 'Pago' : 'Pendente'}
                    />
                    <LabelItem
                      label="Id de Pagamento"
                      value={data?.withdrawSulcredi?.endToEndId ?? data.endToEndId}
                    />
                    <LabelItem
                      label="Pago em"
                      value={DateTime.fromISO(data?.withdrawSulcredi?.updatedAt).toFormat('dd/MM/yyyy HH:mm:ss')}
                    />
                  </Box>
                )}

                {data?.receiver && (
                  <Box
                    mt={4}
                    border="ButtonShadow"
                  >
                    <Text
                      fontWeight="semibold"
                      mb={4}
                    >
                      Comprovante
                    </Text>
                    <HStack spacing={2} mb={2}>
                      <Tooltip
                        label={`Imprimir`}
                        placement={`top`}
                      >
                        <IconButton
                          aria-label="Imprimir"
                          icon={<FiPrinter />}
                          size={`sm`}
                          color={`gray.500`}
                          variant={`outline`}
                          onClick={handlePrint}
                        />
                      </Tooltip>
                      <Tooltip
                        label={`Baixar imagem`}
                        placement={`top`}
                      >
                        <IconButton
                          aria-label="Baixar imagem"
                          icon={<FiDownload />}
                          size={`sm`}
                          color={`gray.500`}
                          variant={`outline`}
                          onClick={downloadImage}
                        />
                      </Tooltip>
                    </HStack>
                    <LabelItem
                      label="Status Pagamento"
                      value={data?.status === 'FINISHED' ? 'Pago' : 'Pendente'}
                    />
                    <LabelItem
                      label="Id de Transação"
                      value={data?.id}
                    />
                    <LabelItem
                      label="Data da Transação"
                      value={DateTime.fromISO(data?.createdAt).toFormat('dd/MM/yyyy HH:mm:ss')}
                    />
                  </Box>
                )}

                {!data?.withdrawSulcredi && !data?.receiver && data.status === 'IN_PAYMENT' && (
                  <Box
                    mt={4}
                    border="ButtonShadow"
                  >
                    <Text
                      fontWeight="semibold"
                      mb={4}
                    >
                      Comprovante
                    </Text>
                    <HStack spacing={2} mb={2}>
                      <Tooltip
                        label={`Imprimir`}
                        placement={`top`}
                      >
                        <IconButton
                          aria-label="Imprimir"
                          icon={<FiPrinter />}
                          size={`sm`}
                          color={`gray.500`}
                          variant={`outline`}
                          onClick={handlePrint}
                        />
                      </Tooltip>
                      <Tooltip
                        label={`Baixar imagem`}
                        placement={`top`}
                      >
                        <IconButton
                          aria-label="Baixar imagem"
                          icon={<FiDownload />}
                          size={`sm`}
                          color={`gray.500`}
                          variant={`outline`}
                          onClick={downloadImage}
                        />
                      </Tooltip>
                    </HStack>
                    <LabelItem
                      label="Status Pagamento"
                      value="Em Pagamento"
                    />
                    <LabelItem
                      label="Id de Transação"
                      value={data?.id}
                    />
                    <LabelItem
                      label="Data da Transação"
                      value={DateTime.fromISO(data?.createdAt).toFormat('dd/MM/yyyy HH:mm:ss')}
                    />
                  </Box>
                )}

                {data.imageUrl && (
                  <Box mt={4}>
                    <Text fontWeight="semibold">Download do comprovante</Text>
                    <Divider mb={2} />
                    <Button
                      colorScheme="green"
                      onClick={() => {
                        saveAs(data.imageUrl);
                      }}
                    >
                      Download
                    </Button>
                  </Box>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        </>
      )}
    </>
  );
};

export default SeeWithdrawel;
